/**
 * Meta Pixel orchestration for the storefront.
 *
 * Resolves the merchant's public Meta Pixel id from integration-service-core
 * (the browser-only "Meta Pixel" integration the merchant connects in the
 * portal) and fires the standard commerce funnel events: PageView, ViewContent,
 * AddToCart, InitiateCheckout and Purchase.
 */

import type Cart from "@/types/cart";
import type CartItem from "@/types/cart-item";
import type Item from "@/types/item";
import { initMetaPixel, trackMetaPixel } from "./fbq";

const INTEGRATION_API_URL = import.meta.env.VITE_INTEGRATION_API_URL as
  | string
  | undefined;

const CURRENCY = "BRL";

export async function initMetaPixelForMerchant(
  merchantId: string | undefined | null,
): Promise<void> {
  if (!INTEGRATION_API_URL || !merchantId) return;

  try {
    const base = INTEGRATION_API_URL.replace(/\/$/, "");
    const res = await fetch(
      `${base}/${encodeURIComponent(merchantId)}/meta-pixel/config`,
    );
    if (!res.ok) return;
    const data = (await res.json()) as { pixelId?: string };
    if (data?.pixelId) {
      initMetaPixel(data.pixelId);
    }
  } catch {
  }
}

export function trackMetaPageView(): void {
  trackMetaPixel("PageView");
}

function cartItemContentId(cartItem: CartItem): string {
  return cartItem.item?.id ?? cartItem.itemOffer?.id ?? cartItem.id;
}

function cartContents(cart: Cart) {
  return cart.items.map((ci) => ({
    id: cartItemContentId(ci),
    quantity: ci.quantity,
    item_price: ci.unitPrice,
  }));
}

function cartItemCount(cart: Cart): number {
  return cart.items.reduce((sum, ci) => sum + ci.quantity, 0);
}

export function trackMetaViewContent(item: Item, price?: number): void {
  trackMetaPixel("ViewContent", {
    currency: CURRENCY,
    value: price,
    content_type: "product",
    content_ids: [item.id],
    content_name: item.name,
  });
}

export function trackMetaAddToCart(cartItem: CartItem): void {
  const id = cartItemContentId(cartItem);
  trackMetaPixel("AddToCart", {
    currency: CURRENCY,
    value: cartItem.itemTotalPrice,
    content_type: "product",
    content_ids: [id],
    contents: [
      { id, quantity: cartItem.quantity, item_price: cartItem.unitPrice },
    ],
  });
}

export function trackMetaInitiateCheckout(cart: Cart): void {
  trackMetaPixel("InitiateCheckout", {
    currency: CURRENCY,
    value: cart.totalPrice,
    content_type: "product",
    content_ids: cart.items.map(cartItemContentId),
    contents: cartContents(cart),
    num_items: cartItemCount(cart),
  });
}

export function trackMetaPurchase(params: {
  cart: Cart;
  value: number;
  orderId: string;
}): void {
  const { cart, value, orderId } = params;
  trackMetaPixel(
    "Purchase",
    {
      currency: CURRENCY,
      value,
      content_type: "product",
      content_ids: cart.items.map(cartItemContentId),
      contents: cartContents(cart),
      num_items: cartItemCount(cart),
    },
    { eventID: orderId },
  );
}
